import { RotateCcw, CheckCircle, Info } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * SteekproefBanner — Shows context banner when a Steekproef (re-assessment) is active.
 * Also displays Nulmeting reference scores so the user knows what was done before.
 * 
 * Props:
 * - active: boolean
 * - moduleName: 'people' | 'process' | 'technology'
 * - moduleLabel: string (e.g. "Mensen")
 * - reassessment: { started_at, modules }
 * - nulmetingSnapshot: { people_score, process_score, technology_score, ... }
 * - isModuleIncluded: boolean (whether this specific module is part of the Steekproef)
 */
export const SteekproefBanner = ({ active, moduleName, moduleLabel, reassessment, nulmetingSnapshot, isModuleIncluded }) => {
  if (!active) return null;

  const scoreKey = `${moduleName}_score`;
  const nulmetingScore = nulmetingSnapshot?.[scoreKey];
  const startDate = reassessment?.started_at
    ? new Date(reassessment.started_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  if (!isModuleIncluded) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-neutral-50/50" data-testid="steekproef-not-included">
        <Info className="h-4 w-4 text-neutral-400 shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neutral-600">
            Er loopt een steekproef, maar <span className="font-semibold">{moduleLabel}</span> maakt er geen deel van uit.
          </p>
          {nulmetingScore !== null && nulmetingScore !== undefined && (
            <p className="text-[11px] text-neutral-400 mt-0.5">Nulmeting score: {nulmetingScore}%</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden" data-testid="steekproef-banner">
      {/* Active banner */}
      <div className="flex items-center gap-3 p-4">
        <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <RotateCcw className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-amber-800">Steekproef actief</p>
            <Badge className="bg-amber-100 text-amber-700 text-[10px]">{moduleLabel}</Badge>
          </div>
          <p className="text-xs text-amber-700 mt-0.5">
            {startDate ? `Gestart op ${startDate}` : 'Voer de taken opnieuw uit voor een actueel beeld.'}
            {' '}Eerder voltooide resultaten tellen niet mee in de voortgang.
          </p>
        </div>
      </div>

      {/* Nulmeting reference */}
      {nulmetingSnapshot && (
        <div className="border-t border-amber-200/60 px-4 py-3 bg-white/40" data-testid="nulmeting-reference">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-3 w-3 text-neutral-400" strokeWidth={1.5} />
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Nulmeting resultaat (referentie)</p>
          </div>
          <div className="flex items-center gap-6">
            {nulmetingScore !== null && nulmetingScore !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">{moduleLabel} score:</span>
                <span className="text-sm font-bold text-neutral-700">{nulmetingScore}%</span>
              </div>
            )}
            {nulmetingSnapshot.scan_summary && (
              <>
                {moduleName === 'people' && nulmetingSnapshot.scan_summary.people_campaigns > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Campagnes:</span>
                    <span className="text-sm font-bold text-neutral-700">{nulmetingSnapshot.scan_summary.people_campaigns}</span>
                  </div>
                )}
                {moduleName === 'process' && nulmetingSnapshot.scan_summary.process_questionnaires > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Vragenlijsten:</span>
                    <span className="text-sm font-bold text-neutral-700">{nulmetingSnapshot.scan_summary.process_questionnaires}</span>
                  </div>
                )}
                {moduleName === 'technology' && nulmetingSnapshot.scan_summary.technology_scans > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Scans:</span>
                    <span className="text-sm font-bold text-neutral-700">{nulmetingSnapshot.scan_summary.technology_scans}</span>
                  </div>
                )}
              </>
            )}
            {nulmetingSnapshot.cwi_score !== null && nulmetingSnapshot.cwi_score !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">CWI:</span>
                <span className="text-sm font-bold text-neutral-700">{nulmetingSnapshot.cwi_score}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SteekproefBanner;
